/**
 * Findings With Remediation Component
 *
 * Displays failed tests with inline expandable remediation guidance.
 * Uses accordion pattern for clean UX - no side panel needed.
 */

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CollapsibleRoot,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  CheckCircle2,
  Loader2,
  ChevronDown,
  ChevronUp,
  Shield,
  Code,
  Lightbulb,
  AlertTriangle,
} from "lucide-react";
import type {
  LLMScanResult,
  SeverityLevel,
  RemediationGuidance,
  ClassifiedIssue,
} from "@/types/llm";
import { classifyIssues as classifyIssuesUtil } from "@/utils/issueClassification";
import { formatPluginName } from "@/utils/llmFormat";

interface FindingsWithRemediationProps {
  results: LLMScanResult[];
  isLoading?: boolean;
  isPartialData?: boolean;
  scanFailed?: boolean;
  blockingIssues?: ClassifiedIssue[];
  advisoryIssues?: ClassifiedIssue[];
}

const getSeverityConfig = (severity: SeverityLevel | null) => {
  if (!severity) return { color: "bg-gray-100 text-gray-800", icon: "text-gray-500" };
  switch (severity) {
    case "critical":
      return { color: "bg-red-100 text-red-800", icon: "text-red-500" };
    case "high":
      return { color: "bg-orange-100 text-orange-800", icon: "text-orange-500" };
    case "medium":
      return { color: "bg-yellow-100 text-yellow-800", icon: "text-yellow-500" };
    case "low":
    default:
      return { color: "bg-gray-100 text-gray-800", icon: "text-gray-500" };
  }
};

const parseRemediationGuidance = (jsonStr: string | null): RemediationGuidance | null => {
  if (!jsonStr) return null;
  try {
    return JSON.parse(jsonStr) as RemediationGuidance;
  } catch {
    return null;
  }
};

const getEffortConfig = (effort: string | undefined) => {
  switch (effort) {
    case "Low":
      return { color: "bg-green-100 text-green-800", label: "Low Effort" };
    case "Medium":
      return { color: "bg-yellow-100 text-yellow-800", label: "Medium Effort" };
    case "High":
      return { color: "bg-red-100 text-red-800", label: "High Effort" };
    default:
      return { color: "bg-gray-100 text-gray-800", label: "Unknown" };
  }
};

interface FindingItemProps {
  result: LLMScanResult | ClassifiedIssue;
  index: number;
}

const FindingItem: React.FC<FindingItemProps> = ({ result, index }) => {
  const [isOpen, setIsOpen] = useState(index === 0); // First item open by default
  const severityConfig = getSeverityConfig(result.severity);
  const remediation = parseRemediationGuidance(result.remediation_guidance);
  const isFailed = result.status === "FAIL";
  const isGenerating = isFailed && !remediation && result.remediation_generated_at === null;
  const hasRemediation = isFailed && remediation !== null;

  return (
    <CollapsibleRoot open={isOpen} onOpenChange={setIsOpen}>
      <div className="max-w-full overflow-hidden rounded-[15px] border bg-white">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-start gap-3 p-4 hover:bg-muted/30 transition-colors cursor-pointer">
            {/* Number Badge */}
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${severityConfig.color}`}
            >
              {index + 1}
            </div>

            {/* Content */}
            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-semibold text-sm">{formatPluginName(result.plugin_id)}</span>
                {result.severity && (
                  <Badge className={`${severityConfig.color} text-xs px-2 py-0`}>
                    {result.severity.toUpperCase()}
                  </Badge>
                )}
                {hasRemediation && (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Fix Available
                  </Badge>
                )}
              </div>
              {result.goal && (
                <p className="text-xs text-muted-foreground line-clamp-2">{result.goal}</p>
              )}
              {isFailed && isGenerating && (
                <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Generating remediation...</span>
                </div>
              )}
              {isFailed && !isOpen && hasRemediation && (
                <p className="text-xs text-blue-600 mt-1">
                  Click to see details and recommended fix
                </p>
              )}
            </div>

            {/* Expand/Collapse Icon */}
            <div className="flex-shrink-0 text-muted-foreground">
              {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 border-t bg-muted/20 overflow-hidden">
            {/* Risk Explanation - Only for failed tests */}
            {isFailed && remediation && (
              <div className="pt-4 space-y-4 overflow-hidden">
                {/* Risk Alert */}
                <div className="flex gap-3 rounded-[15px] border border-orange-100 bg-orange-50 p-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-orange-800 mb-1">Risk Assessment</p>
                    <p className="text-sm text-orange-700 break-words whitespace-normal">
                      {remediation.risk_explanation}
                    </p>
                  </div>
                </div>

                {/* Remediation Steps */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold">How to Fix</span>
                    <Badge
                      className={`${getEffortConfig(remediation.estimated_effort).color} text-xs`}
                    >
                      {getEffortConfig(remediation.estimated_effort).label}
                    </Badge>
                  </div>
                  <ul className="list-none space-y-1 ml-6">
                    {remediation.remediation_steps.map((step, idx) => (
                      <li
                        key={idx}
                        className="text-sm text-muted-foreground break-words whitespace-normal"
                      >
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Code Example */}
                {remediation.code_example && (
                  <div className="space-y-2 overflow-hidden">
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-semibold">Code Example</span>
                    </div>
                    <div className="w-full max-w-full overflow-x-auto">
                      <pre className="max-w-full whitespace-pre-wrap break-words rounded-[15px] bg-gray-900 p-3 text-xs text-gray-100">
                        <code>{remediation.code_example}</code>
                      </pre>
                    </div>
                  </div>
                )}

                {/* Compliance Impact */}
                {remediation.compliance_impact.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-semibold">Compliance Impact</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {remediation.compliance_impact.map((framework, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {framework}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Loading State - Only for failed tests */}
            {isFailed && isGenerating && (
              <div className="pt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>AI is generating remediation guidance...</span>
                </div>
              </div>
            )}

            {/* No Remediation Yet - Only for failed tests */}
            {isFailed && !remediation && !isGenerating && (
              <div className="pt-4">
                <p className="text-sm text-muted-foreground">
                  Remediation guidance not yet available.
                </p>
              </div>
            )}

            {/* Test Result Reason (always show) */}
            {result.reason && (
              <div className="pt-4 mt-4 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {isFailed ? "Failure Reason" : "Test Result"}
                </p>
                <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
                  {result.reason}
                </p>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </CollapsibleRoot>
  );
};

export const FindingsWithRemediation: React.FC<FindingsWithRemediationProps> = ({
  results,
  isLoading = false,
  isPartialData = false,
  scanFailed = false,
  blockingIssues = [],
  advisoryIssues = [],
}) => {
  if (isLoading) {
    return (
      <Card className="rounded-[15px] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle>Blocking Issues</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Use provided classified issues if available, otherwise classify from results
  const failedBlocking =
    blockingIssues.length > 0
      ? blockingIssues
      : classifyIssuesUtil(results).filter((r) => r.status === "FAIL" && r.category === "blocking");

  const failedAdvisory =
    advisoryIssues.length > 0
      ? advisoryIssues
      : classifyIssuesUtil(results).filter((r) => r.status === "FAIL" && r.category === "advisory");

  // Sort by severity
  const sortBySeverity = (
    a: LLMScanResult | ClassifiedIssue,
    b: LLMScanResult | ClassifiedIssue
  ) => {
    const severityOrder: Record<SeverityLevel | "unknown", number> = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
      unknown: 4,
    };
    const aSeverity = a.severity || "unknown";
    const bSeverity = b.severity || "unknown";
    return severityOrder[aSeverity] - severityOrder[bSeverity];
  };

  const sortedBlocking = [...failedBlocking].sort(sortBySeverity);
  const sortedAdvisory = [...failedAdvisory].sort(sortBySeverity);

  if (sortedBlocking.length === 0 && sortedAdvisory.length === 0) {
    const cardStyle = scanFailed
      ? "border-red-200 bg-red-50/30"
      : isPartialData
        ? "border-blue-200 bg-blue-50/30"
        : "border-green-200 bg-green-50/30";

    const icon = scanFailed ? (
      <AlertTriangle className="h-5 w-5 text-red-600" />
    ) : isPartialData ? (
      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
    ) : (
      <CheckCircle2 className="h-5 w-5 text-green-600" />
    );

    const title = scanFailed
      ? "Scan Failed"
      : isPartialData
        ? "Scan In Progress"
        : "All Tests Passed";

    const description = scanFailed
      ? "The scan could not complete. No security tests were run."
      : isPartialData
        ? "No issues found so far, but the scan is still running. New findings may appear as more tests complete."
        : "No security vulnerabilities detected. Your LLM application passed all security tests.";

    return (
      <Card className={cardStyle}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{description}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div id="findings-section" className="space-y-6">
      {/* Blocking Issues Section */}
      {sortedBlocking.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3>Blocking Issues</h3>
              <Badge variant="destructive" className="text-xs">
                {sortedBlocking.length} {sortedBlocking.length === 1 ? "issue" : "issues"}
              </Badge>
              <Badge variant="outline" className="text-xs border-red-200 text-red-700">
                Must fix to pass
              </Badge>
              {isPartialData && (
                <Badge variant="outline" className="text-xs">
                  Partial data
                </Badge>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {sortedBlocking.map((result, index) => (
              <FindingItem key={result.uuid} result={result} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* Advisory Issues Section */}
      {sortedAdvisory.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3>Advisory Issues</h3>
              <Badge variant="outline" className="text-xs">
                {sortedAdvisory.length} {sortedAdvisory.length === 1 ? "issue" : "issues"}
              </Badge>
              <Badge variant="outline" className="text-xs border-yellow-200 text-yellow-700">
                Fix later
              </Badge>
              {isPartialData && (
                <Badge variant="outline" className="text-xs">
                  Partial data
                </Badge>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {sortedAdvisory.map((result, index) => (
              <FindingItem
                key={result.uuid}
                result={result}
                index={index + sortedBlocking.length}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
